/**
 * Chat — one thread per customer, shared by everyone who touches that customer.
 *
 * Two decisions from the requirements shape this screen:
 *
 *   ONE THREAD FOR LIFE. No per-order rooms. So the message list has to page
 *   backwards cheaply and open at the bottom, because a two-year-old thread has
 *   thousands of messages and nobody wants to see message one.
 *
 *   AN INTERNAL TWIN. Staff get a second tab on the same screen that the
 *   customer cannot see. The tab is visually distinct — not just labelled —
 *   because the cost of typing "he still hasn't paid" into the wrong tab is
 *   paid by the business, not by the app.
 *
 * Messages can be deleted by their sender within five minutes and not after.
 * The countdown is shown rather than the button silently disappearing.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Page } from '../../components/Shell';
import {
  AuthImage, Banner, Button, Card, Chip, ConfirmSheet, Empty, ErrorState, Skeleton,
  useToast,
} from '../../components/ui';
import { IconChat, IconChevron, IconSend, IconImage } from '../../components/Icons';
import { useQuery, useOnline, usePoll, useRefreshOnFocus } from '../../lib/useApi';
import { api, ApiError, uploadMedia } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { useT, useI18n } from '../../lib/i18n';
import { relative, timeShort, dateShort } from '../../lib/format';
import { capturePhoto } from '../../lib/camera';
import * as outbox from '../../lib/outbox';

// ---------------------------------------------------------------------------
// Thread list
// ---------------------------------------------------------------------------

interface Thread {
  id: string;
  type: 'customer' | 'internal';
  customerId: string | null;
  customerName: string | null;
  customerCode: string | null;
  title: string | null;
  lastMessageAt: string | null;
  preview: string | null;
  messageCount: number;
  unreadCount: number;
  isInternal: boolean;
}

export function ThreadList() {
  const t = useT();
  const { locale } = useI18n();
  const navigate = useNavigate();
  const { user } = useAuth();

  const { data, loading, error, refetch } = useQuery<{ threads: Thread[] }>('/api/chat/threads');

  // Polling, not sockets. Durable Objects are what a real-time channel on
  // Workers would need, and they are not on this plan — see the tech-stack
  // note in the master document. Thirty seconds is the compromise, and push
  // notifications cover anything urgent.
  // Poll while visible (usePoll pauses in a backgrounded tab, so it stops
  // draining battery/data), and refetch the moment the screen is returned to.
  usePoll(refetch, 30000);
  useRefreshOnFocus(refetch);

  // A customer has exactly one conversation, so the list is skipped entirely.
  useEffect(() => {
    if (user?.role !== 'customer' || !user.customerId) return;
    navigate(`/chat/${user.customerId}`, { replace: true });
  }, [user, navigate]);

  const threads = useMemo(
    () => (data?.threads ?? [])
      // The internal twin is not a separate row — it is a tab inside its
      // customer thread. Showing both would double the list.
      .filter((x) => !x.isInternal && !!x.customerId)
      .sort((a, b) => {
        if ((b.unreadCount > 0 ? 1 : 0) !== (a.unreadCount > 0 ? 1 : 0)) {
          return (b.unreadCount > 0 ? 1 : 0) - (a.unreadCount > 0 ? 1 : 0);
        }
        return (b.lastMessageAt ?? '').localeCompare(a.lastMessageAt ?? '');
      }),
    [data],
  );

  const totalUnread = threads.reduce((s, x) => s + x.unreadCount, 0);

  if (loading && !data) return <Page title={t('chat.title')}><Skeleton rows={5} /></Page>;

  return (
    <Page title={t('chat.title')} badges={{ chat: totalUnread }}>
      <div className="stack">
        {error && !data && <ErrorState message={error.message} onRetry={refetch} />}
        {data && threads.length === 0 && <Empty title={t('chat.noThreads')} />}

        <Card flush>
          <div className="list">
            {threads.map((thread) => (
              <button
                key={thread.id}
                className="list__item"
                onClick={() => navigate(`/chat/${thread.customerId}`)}
              >
                <span className="avatar" aria-hidden>
                  <IconChat width={18} height={18} />
                </span>
                <span className="grow" style={{ minWidth: 0, textAlign: 'left' }}>
                  <span className="row" style={{ gap: 6 }}>
                    <span className="t-semi t-clip">
                      {thread.customerName ?? thread.title ?? 'Conversation'}
                    </span>
                    {thread.unreadCount > 0 && <Chip tone="danger">{thread.unreadCount}</Chip>}
                  </span>
                  {thread.preview && (
                    <span className="t-sm t-muted t-clip" style={{ display: 'block' }}>
                      {thread.preview}
                    </span>
                  )}
                </span>
                <span className="t-xs t-muted" style={{ whiteSpace: 'nowrap' }}>
                  {thread.lastMessageAt ? relative(thread.lastMessageAt, locale) : ''}
                </span>
                <IconChevron width={18} height={18} style={{ color: 'var(--faint)' }} />
              </button>
            ))}
          </div>
        </Card>
      </div>
    </Page>
  );
}

// ---------------------------------------------------------------------------
// Message view
// ---------------------------------------------------------------------------

interface Message {
  id: string;
  senderUserId: string;
  senderRole: string;
  senderName: string;
  type: 'text' | 'image' | 'system';
  body: string | null;
  isDeleted: boolean;
  systemEvent: string | null;
  sentAt: string;
  isOwn: boolean;
  canDelete: boolean;
  deleteAllowedUntil: string | null;
  media: {
    id: string; url: string; thumbnailUrl: string | null;
    mimeType: string; widthPx: number | null; heightPx: number | null;
  } | null;
}

interface MessagesResponse {
  threadId: string;
  threadType: 'customer' | 'internal';
  messages: Message[];
  hasMore: boolean;
  oldestSentAt: string | null;
}

/** A message queued on this device and not yet acknowledged by the server. */
interface Outgoing {
  localId: string;
  body: string | null;
  previewUrl: string | null;
  failed: boolean;
}

export function ThreadView() {
  const t = useT();
  const { locale } = useI18n();
  const toast = useToast();
  const online = useOnline();
  const { user } = useAuth();
  const params = useParams();
  const customerId = params.customerId ?? '';

  const isStaff = !!user && user.role !== 'customer';

  const [tab, setTab] = useState<'customer' | 'internal'>('customer');
  const [older, setOlder] = useState<Message[]>([]);
  const [outgoing, setOutgoing] = useState<Outgoing[]>([]);
  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);
  const [loadingOlder, setLoadingOlder] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState<Message | null>(null);
  const [zoom, setZoom] = useState<string | null>(null);

  const bottomRef = useRef<HTMLDivElement>(null);
  const scrollerRef = useRef<HTMLDivElement>(null);

  // One call resolves both threads for this customer. It also registers the
  // viewer as a participant, which is what makes unread counts work — so it
  // has to happen before any message fetch.
  const { data: pair, loading: resolving, error: resolveError } = useQuery<{
    customerThreadId: string; internalThreadId: string | null; title: string | null;
  }>(customerId ? `/api/chat/threads/for-customer/${customerId}` : null);

  const customerThreadId = pair?.customerThreadId ?? null;
  const internalId = isStaff ? pair?.internalThreadId ?? null : null;
  const activeId = tab === 'internal' && internalId ? internalId : customerThreadId;

  const { data: main, refetch: refetchMain } = useQuery<MessagesResponse>(
    customerThreadId ? `/api/chat/threads/${customerThreadId}/messages?limit=50` : null,
  );

  const { data: internal, refetch: refetchInternal } = useQuery<MessagesResponse>(
    tab === 'internal' && internalId ? `/api/chat/threads/${internalId}/messages?limit=50` : null,
  );

  const active = tab === 'internal' ? internal : main;
  const refetchActive = tab === 'internal' ? refetchInternal : refetchMain;

  // Poll while the screen is open (paused when backgrounded), as on the list.
  usePoll(refetchActive, 20000);

  // Switching tabs discards the older-message window: it belongs to the thread
  // that was open, not this one.
  useEffect(() => { setOlder([]); }, [tab]);

  // Open at the bottom, which is where the conversation is.
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ block: 'end' });
  }, [active?.messages.length, tab]);

  const messages = useMemo(() => {
    const merged = [...older, ...(active?.messages ?? [])];
    const seen = new Set<string>();
    return merged.filter((m) => (seen.has(m.id) ? false : (seen.add(m.id), true)));
  }, [older, active]);

  const loadOlder = async () => {
    const oldest = messages[0]?.sentAt ?? active?.oldestSentAt;
    if (!oldest || !activeId) return;
    setLoadingOlder(true);
    try {
      const page = await api.get<MessagesResponse>(
        `/api/chat/threads/${activeId}/messages?limit=50&before=${encodeURIComponent(oldest)}`,
      );
      setOlder((prev) => [...page.messages, ...prev]);
    } catch (err) {
      toast.show(err instanceof Error ? err.message : 'Failed', 'danger');
    } finally {
      setLoadingOlder(false);
    }
  };

  const send = useCallback(async (body: string | null, photo?: { blob: Blob; url: string }) => {
    if (!activeId) return;
    if (!body?.trim() && !photo) return;

    const localId = outbox.newUuid();
    setOutgoing((prev) => [...prev, {
      localId,
      body: body?.trim() ?? null,
      previewUrl: photo?.url ?? null,
      failed: false,
    }]);
    setText('');
    setSending(true);

    try {
      if (photo) {
        // Offline, the image is stashed and the message queued; the outbox
        // uploads the photo first and substitutes the real media id.
        if (!online) {
          const localMediaId = await outbox.stashMedia(photo.blob, 'chat_image');
          await outbox.enqueue({
            method: 'POST',
            path: `/api/chat/threads/${activeId}/messages`,
            body: { body: body?.trim() ?? undefined, mediaId: 'PENDING' },
            label: `${t('chat.title')} — ${t('app.photo')}`,
            kind: 'other',
            mediaIds: [localMediaId],
          });
          toast.show(t('app.offlineHint'));
          setOutgoing((prev) => prev.filter((x) => x.localId !== localId));
          return;
        }

        const uploaded = await uploadMedia({
          blob: photo.blob,
          purpose: 'chat_image',
          entityType: 'chat_thread',
          entityId: activeId,
        });
        await api.post(`/api/chat/threads/${activeId}/messages`, {
          ...(body?.trim() ? { body: body.trim() } : {}),
          mediaId: uploaded.mediaId,
          clientUuid: localId,
        });
      } else {
        if (!online) {
          await outbox.enqueue({
            method: 'POST',
            path: `/api/chat/threads/${activeId}/messages`,
            body: { body: body!.trim() },
            label: `${t('chat.title')}: ${body!.trim().slice(0, 30)}`,
            kind: 'other',
          });
          setOutgoing((prev) => prev.filter((x) => x.localId !== localId));
          toast.show(t('app.offlineHint'));
          return;
        }
        await api.post(`/api/chat/threads/${activeId}/messages`, {
          body: body!.trim(),
          clientUuid: localId,
        });
      }

      setOutgoing((prev) => prev.filter((x) => x.localId !== localId));
      await refetchActive();
    } catch (err) {
      // The draft is kept visible and marked failed rather than vanishing —
      // losing what someone typed is worse than showing it did not send.
      setOutgoing((prev) => prev.map((x) => (x.localId === localId ? { ...x, failed: true } : x)));
      toast.show(err instanceof ApiError ? err.message : 'Message not sent', 'danger');
    } finally {
      setSending(false);
    }
  }, [activeId, online, refetchActive, t, toast]);

  const attach = async () => {
    // 2 MB cap, images only — the agreed rule. Compression happens before the
    // blob leaves the device.
    const image = await capturePhoto({ maxBytes: 2 * 1024 * 1024, maxDimension: 1600 });
    if (!image) return;
    await send(text || null, { blob: image.blob, url: URL.createObjectURL(image.blob) });
  };

  const remove = async (message: Message) => {
    try {
      await api.del(`/api/chat/messages/${message.id}`);
      setConfirmDelete(null);
      setOlder((prev) => prev.map((m) => (m.id === message.id
        ? { ...m, isDeleted: true, body: null, media: null, canDelete: false }
        : m)));
      await refetchActive();
    } catch (err) {
      // The five-minute window is enforced by the server; if it has passed,
      // its message says so.
      toast.show(err instanceof Error ? err.message : 'Could not delete', 'danger');
      setConfirmDelete(null);
    }
  };

  if (!customerId) return <Empty title={t('chat.noThreads')} />;
  if (resolveError && !pair) {
    return (
      <Page title={t('chat.title')} back="/chat">
        <ErrorState message={resolveError.message} />
      </Page>
    );
  }

  return (
    <Page
      title={pair?.title ?? t('chat.title')}
      subtitle={tab === 'internal' ? t('chat.internal') : undefined}
      // A customer keeps their tab bar; staff arrived from the list and get a
      // back button instead, so the thread has the full height.
      back={isStaff ? '/chat' : undefined}
      hideNav={isStaff}
    >
      <div className="chat">
        {isStaff && internalId && (
          <div className="segment" role="tablist" style={{ marginBottom: 12 }}>
            <button type="button" aria-pressed={tab === 'customer'} onClick={() => setTab('customer')}>
              {t('chat.customerTab')}
            </button>
            <button type="button" aria-pressed={tab === 'internal'} onClick={() => setTab('internal')}>
              {t('chat.internal')}
            </button>
          </div>
        )}

        {tab === 'internal' && (
          <Banner tone="warn" title={t('chat.internal')}>
            <span className="t-sm">{t('chat.internalHint')}</span>
          </Banner>
        )}

        <div
          className={['chat__scroll', tab === 'internal' ? 'chat__scroll--internal' : '']
            .filter(Boolean).join(' ')}
          ref={scrollerRef}
        >
          {(resolving || !active) && <Skeleton rows={4} height={48} />}

          {active?.hasMore && (
            <div className="center" style={{ padding: 8 }}>
              <Button size="sm" variant="quiet" busy={loadingOlder} onClick={() => void loadOlder()}>
                {t('app.showMore')}
              </Button>
            </div>
          )}

          {messages.map((message, index) => {
            const previous = messages[index - 1];
            const newDay = !previous
              || previous.sentAt.slice(0, 10) !== message.sentAt.slice(0, 10);

            return (
              <div key={message.id}>
                {newDay && (
                  <div className="chat__day">{dateShort(message.sentAt)}</div>
                )}

                {message.type === 'system' ? (
                  <div className="chat__system">
                    {message.senderName} {message.systemEvent === 'driver_joined'
                      ? t('chat.driverJoined')
                      : message.systemEvent === 'driver_left'
                        ? t('chat.driverLeft')
                        : (message.systemEvent ?? '').replace(/_/g, ' ')}
                  </div>
                ) : (
                  <div className={['bubble', message.isOwn ? 'bubble--own' : ''].filter(Boolean).join(' ')}>
                    {!message.isOwn && (
                      <div className="bubble__who">
                        {message.senderName}
                        <span className="t-xs t-muted"> · {message.senderRole.replace(/_/g, ' ')}</span>
                      </div>
                    )}

                    {message.isDeleted ? (
                      <div className="t-sm t-muted" style={{ fontStyle: 'italic' }}>
                        {t('chat.deleted')}
                      </div>
                    ) : (
                      <>
                        {message.media && (
                          <AuthImage
                            path={message.media.thumbnailUrl ?? message.media.url}
                            alt=""
                            style={{ width: 180, height: 180, flex: 'unset', marginBottom: message.body ? 6 : 0 }}
                            onClick={() => setZoom(message.media!.url)}
                          />
                        )}
                        {message.body && <div className="bubble__body">{message.body}</div>}
                      </>
                    )}

                    <div className="bubble__meta">
                      {timeShort(message.sentAt)}
                      {message.canDelete && (
                        <button
                          className="linkbtn"
                          onClick={() => setConfirmDelete(message)}
                        >
                          {t('app.remove')}
                        </button>
                      )}
                    </div>
                  </div>
                )}
              </div>
            );
          })}

          {outgoing.map((item) => (
            <div key={item.localId} className="bubble bubble--own bubble--pending">
              {item.previewUrl && (
                <img
                  src={item.previewUrl}
                  alt=""
                  className="thumb"
                  style={{ width: 180, height: 180, flex: 'unset', marginBottom: item.body ? 6 : 0 }}
                />
              )}
              {item.body && <div className="bubble__body">{item.body}</div>}
              <div className="bubble__meta">
                {item.failed ? (
                  <span className="t-danger">
                    {t('app.syncFailed', { n: 1 })}
                    <button
                      className="linkbtn"
                      onClick={() => {
                        setOutgoing((prev) => prev.filter((x) => x.localId !== item.localId));
                        void send(item.body);
                      }}
                    >
                      {t('app.retry')}
                    </button>
                  </span>
                ) : (
                  <span className="t-muted">{t('app.syncing')}</span>
                )}
              </div>
            </div>
          ))}

          <div ref={bottomRef} />
        </div>

        <div className="composer">
          <button
            className="icon-btn"
            aria-label={t('app.photo')}
            onClick={() => void attach()}
            disabled={sending}
          >
            <IconImage width={22} height={22} />
          </button>
          <textarea
            className="composer__input"
            rows={1}
            value={text}
            placeholder={t('chat.typeMessage')}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={(e) => {
              // Enter sends on a desktop keyboard; Shift+Enter makes a line
              // break. On a phone the on-screen Enter inserts a newline, which
              // is why the send button is always present.
              if (e.key === 'Enter' && !e.shiftKey && !('ontouchstart' in window)) {
                e.preventDefault();
                void send(text);
              }
            }}
          />
          <button
            className="icon-btn icon-btn--primary"
            aria-label={t('chat.send')}
            disabled={sending || (!text.trim())}
            onClick={() => void send(text)}
          >
            <IconSend width={20} height={20} />
          </button>
        </div>

        <div className="t-xs t-muted center" style={{ paddingTop: 4 }}>
          {t('chat.imageOnly')}
        </div>
      </div>

      <ConfirmSheet
        open={confirmDelete !== null}
        onClose={() => setConfirmDelete(null)}
        onConfirm={() => { if (confirmDelete) void remove(confirmDelete); }}
        title={t('app.remove')}
        tone="danger"
        confirmLabel={t('app.remove')}
        body={
          confirmDelete?.deleteAllowedUntil
            ? t('chat.deleteWindow', {
              minutes: Math.max(0, Math.ceil(
                (Date.parse(confirmDelete.deleteAllowedUntil) - Date.now()) / 60000,
              )),
            })
            : undefined
        }
      />

      {zoom && (
        <div className="lightbox" onClick={() => setZoom(null)} role="dialog" aria-label={t('app.photo')}>
          <AuthImage
            path={zoom}
            alt=""
            style={{ width: 'auto', height: 'auto', maxWidth: '100%', maxHeight: '100%', flex: 'unset' }}
          />
        </div>
      )}
    </Page>
  );
}
